import { useEffect, useRef } from "react";
import { Avatar, Box, ButtonBase, Dialog, IconButton, Stack, Tooltip, Typography, useMediaQuery } from "@mui/material";
import { useSelector } from "react-redux";
import { alpha, useTheme } from "@mui/material/styles";
import PropTypes from "prop-types";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ZoomInRoundedIcon from "@mui/icons-material/ZoomInRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  avatarColor,
  compactCount,
  getAuthorLabel,
  getAvatarUrl,
  getMediaInfo,
  initials,
  timeAgo,
} from "./communityUtils";
import useQuestionThread from "./useQuestionThread";
import { CommentComposer, CommentList } from "./DiscussionThread";

/** Magnifier pill shown on top of an image; opens the zoomable lightbox. */
export function ZoomButton({ onClick, sx, className }) {
  return (
    <Tooltip title="Zoom image">
      <IconButton
        className={className}
        aria-label="Zoom image"
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        sx={{
          position: "absolute",
          top: 10,
          right: 10,
          width: 36,
          height: 36,
          color: "#fff",
          bgcolor: "rgba(2, 6, 23, 0.5)",
          border: "1px solid rgba(255,255,255,0.18)",
          backdropFilter: "blur(8px)",
          transition: "opacity 160ms ease, transform 160ms ease, background 160ms ease",
          "&:hover": { bgcolor: "rgba(2, 6, 23, 0.72)", transform: "scale(1.06)" },
          ...sx,
        }}
      >
        <ZoomInRoundedIcon sx={{ fontSize: 20 }} />
      </IconButton>
    </Tooltip>
  );
}

ZoomButton.propTypes = { onClick: PropTypes.func.isRequired, sx: PropTypes.object, className: PropTypes.string };

/** Expanded post: large media on the left, post + live discussion on the right. */
function PostDialog({ item, onClose, onZoomImage, onOpenProfile, focusComposer = false }) {
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { url: mediaUrl, posterUrl, isVideo } = getMediaInfo(item);
  const hasMedia = Boolean(mediaUrl);
  const author = getAuthorLabel(item);
  const created = item?.createdAt || item?.created_at || "";
  const body = item?.body || item?.first_user_message || "";
  const me = useSelector((st) => st?.auth || {});
  const thread = useQuestionThread(item?.id);
  const commentCount = thread.question ? thread.comments.length : Number(item?.commentCount) || 0;
  const scrollRef = useRef(null);
  const composerRef = useRef(null);
  const lastCount = useRef(0);
  const hairline = dark ? alpha("#fff", 0.1) : "rgba(15, 23, 42, 0.06)";
  const muted = dark ? "#94A3B8" : "#64748b";
  const softMuted = dark ? "#64748B" : "#94a3b8";

  // Follow the thread down when a new comment appears (not on first load).
  useEffect(() => {
    const el = scrollRef.current;
    if (el && lastCount.current && thread.comments.length > lastCount.current) {
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    }
    lastCount.current = thread.comments.length;
  }, [thread.comments.length]);

  useEffect(() => {
    lastCount.current = 0;
  }, [item?.id]);

  return (
    <Dialog
      open={Boolean(item)}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth={false}
      slotProps={{ backdrop: { sx: { bgcolor: "rgba(2, 6, 23, 0.55)", backdropFilter: "blur(8px)" } } }}
      PaperProps={{
        sx: {
          m: 0,
          width: fullScreen ? "100%" : hasMedia ? "min(1240px, 95vw)" : "min(620px, 94vw)",
          height: fullScreen ? "100%" : "min(780px, 90vh)",
          maxHeight: fullScreen ? "100%" : "90vh",
          borderRadius: fullScreen ? 0 : 4,
          overflow: "hidden",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          bgcolor: dark ? "#0F172A" : "background.paper",
          border: dark ? `1px solid ${alpha("#fff", 0.08)}` : "none",
          boxShadow: dark
            ? "0 40px 120px rgba(0, 0, 0, 0.55)"
            : "0 40px 120px rgba(2, 6, 23, 0.35)",
        },
      }}
    >
      {hasMedia ? (
        <Box
          sx={{
            position: "relative",
            flex: { xs: "0 0 auto", md: 1 },
            height: { xs: "38vh", md: "auto" },
            minWidth: 0,
            bgcolor: "#0b1020",
            backgroundImage: "radial-gradient(ellipse at 50% 40%, #1e293b 0%, #0b1020 75%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isVideo ? (
            <Box
              component="video"
              controls
              autoPlay
              src={mediaUrl}
              poster={posterUrl || undefined}
              sx={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
            />
          ) : (
            <>
              <Box
                component="img"
                src={mediaUrl}
                alt=""
                onClick={() => onZoomImage(mediaUrl)}
                sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", display: "block", cursor: "zoom-in" }}
              />
              <ZoomButton onClick={() => onZoomImage(mediaUrl)} sx={{ top: 16, right: 16, width: 40, height: 40 }} />
            </>
          )}
        </Box>
      ) : null}

      <Box
        sx={{
          width: { xs: "100%", md: hasMedia ? 440 : "100%" },
          flex: { xs: 1, md: "0 0 auto" },
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          bgcolor: dark ? "#111827" : "background.card",
          borderLeft: hasMedia ? { md: `1px solid ${hairline}` } : "none",
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${hairline}` }}
        >
          <ButtonBase
            onClick={() => onOpenProfile(item)}
            sx={{ borderRadius: 999, gap: 1.5, flex: 1, minWidth: 0, justifyContent: "flex-start", textAlign: "left" }}
          >
            <Avatar
              src={getAvatarUrl(item) || undefined}
              alt={author}
              sx={{ width: 44, height: 44, fontSize: 15, fontWeight: 700, color: "#fff", bgcolor: avatarColor(author) }}
            >
              {initials(author)}
            </Avatar>
            <Box minWidth={0}>
              <Typography
                sx={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: dark ? "#F8FAFC" : "ink.strong",
                  lineHeight: 1.25,
                }}
                noWrap
              >
                {author}
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: muted, mt: 0.25 }}>{timeAgo(created)}</Typography>
            </Box>
          </ButtonBase>
          <IconButton
            aria-label="Close"
            onClick={onClose}
            sx={{
              color: dark ? "#E2E8F0" : "inherit",
              bgcolor: dark ? alpha("#fff", 0.08) : "#F1F5F9",
              "&:hover": { bgcolor: dark ? alpha("#fff", 0.14) : "#E2E8F0" },
            }}
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Stack>

        {/* Post + discussion */}
        <Box ref={scrollRef} sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 2.5, pt: 2.25, pb: 2 }}>
          {body ? (
            <Typography
              sx={{
                fontSize: hasMedia ? 15.5 : 17,
                lineHeight: 1.65,
                color: dark ? "#F1F5F9" : "ink.main",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {body}
            </Typography>
          ) : null}

          <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: body ? 1.5 : 0, color: softMuted }}>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ fontSize: 13, fontWeight: 600 }}>
              <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 16 }} />
              <span>{compactCount(commentCount)}</span>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ fontSize: 13, fontWeight: 600 }}>
              <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
              <span>{compactCount(item?.viewCount)}</span>
            </Stack>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mt: 2.5, mb: 2 }}>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 800,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: muted,
              }}
            >
              Discussion
            </Typography>
            <Box flex={1} height="1px" bgcolor={hairline} />
          </Stack>

          <CommentList
            comments={thread.comments}
            loading={thread.loading}
            authorId={item?.user?.id}
            me={me}
            onDelete={thread.removeComment}
            onReply={(name) => composerRef.current?.mention(name)}
          />
        </Box>

        {/* Composer */}
        <Box
          sx={{
            px: 2,
            pt: 1.5,
            pb: { xs: "max(12px, env(safe-area-inset-bottom))", md: 2 },
            borderTop: `1px solid ${hairline}`,
            bgcolor: dark ? "#0F172A" : "background.card",
          }}
        >
          <CommentComposer
            key={item?.id}
            ref={composerRef}
            me={me}
            posting={thread.posting}
            autoFocus={focusComposer}
            onSubmit={(text) => thread.addComment(text, me)}
          />
        </Box>
      </Box>
    </Dialog>
  );
}

PostDialog.propTypes = {
  item: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onZoomImage: PropTypes.func.isRequired,
  onOpenProfile: PropTypes.func.isRequired,
  focusComposer: PropTypes.bool,
};

export default PostDialog;
